import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X, Inbox, ArrowLeft } from 'lucide-react';
import { apiService } from '../services/api';
import { Album, AlbumMedia } from '../types';
import { HeroBackground, ShareButton, PageHeader } from '../components';

interface MediaModalProps {
  media: AlbumMedia | null;
  onClose: () => void;
}

const MediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
  if (!media) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const mediaUrl = apiService.getAlbumMediaUrl(media.id);

  return (
    <div
      className='fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4'
      onClick={handleBackdropClick}
    >
      <div className='relative max-w-4xl max-h-full rounded-lg overflow-hidden'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 z-10'
        >
          <X className='w-4 h-4' />
        </button>

        {media.file_type === 'image' ? (
          <img
            src={mediaUrl}
            alt={media.title || media.filename}
            className='max-w-full max-h-[80vh] object-contain'
          />
        ) : (
          <video
            src={mediaUrl}
            controls
            className='max-w-full max-h-[80vh]'
            autoPlay
          >
            Votre navigateur ne supporte pas la lecture vidéo.
          </video>
        )}

        {(media.title || media.description) && (
          <div className='p-4 bg-white'>
            {media.title && (
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                {media.title}
              </h3>
            )}
            {media.description && (
              <p className='text-gray-600 text-sm'>{media.description}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const AlbumPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [album, setAlbum] = useState<Album | null>(null);
  const [mediaItems, setMediaItems] = useState<AlbumMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<AlbumMedia | null>(null);

  useEffect(() => {
    const fetchAlbum = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const albumId = parseInt(id, 10);
        
        // Backend now handles public access for published albums
        // Try to fetch album - this will work for published albums even without auth
        let albumData: Album | null = null;
        let mediaItemsData: AlbumMedia[] = [];

        try {
          // Fetch album - backend allows public access for published albums
          albumData = await apiService.getAlbum(albumId);
          
          // Fetch media - backend allows public access for published albums
          try {
            const mediaData = await apiService.getAlbumMedia(albumId);
            mediaItemsData = mediaData.media_items;
          } catch (mediaErr: any) {
            // If media fetch fails, continue without media
            console.warn('Could not fetch media:', mediaErr);
          }
        } catch (err: any) {
          // If album fetch fails, show error
          console.error('Error fetching album:', err);
          if (err.response?.status === 404) {
            setError('Album non trouvé');
          } else if (err.response?.status === 403) {
            setError('Vous n\'avez pas l\'autorisation de voir cet album');
          } else {
            setError('Erreur lors du chargement de l\'album');
          }
          return;
        }

        if (albumData) {
          setAlbum(albumData);
          setMediaItems(mediaItemsData);
        } else {
          setError('Album non trouvé');
        }
      } catch (err) {
        console.error('Error fetching album:', err);
        setError('Album non trouvé ou non accessible');
      } finally {
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [id]);

  const handleMediaClick = (media: AlbumMedia) => {
    setSelectedMedia(media);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

  const handleImageError = (mediaId: number) => {
    setBrokenImages((prev) => new Set(prev).add(mediaId));
  };

  // Calculate column count based on screen size
  const getColumnCount = () => {
    if (typeof window === 'undefined') return 3;
    const width = window.innerWidth;
    if (width < 640) return 1; // sm
    if (width < 1024) return 2; // md
    if (width < 1280) return 3; // lg
    return 4; // xl
  };

  const [columnCount, setColumnCount] = useState(getColumnCount());

  useEffect(() => {
    const handleResize = () => {
      setColumnCount(getColumnCount());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div className='min-h-screen bg-church-light flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-church-brown mx-auto mb-4'></div>
          <p className='text-gray-600'>Chargement de l'album...</p>
        </div>
      </div>
    );
  }

  if (error || !album) {
    return (
      <div className='min-h-screen bg-church-light flex items-center justify-center'>
        <div className='text-center'>
          <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
            Album non trouvé
          </h2>
          <p className='text-gray-600 mb-4'>{error || "Cet album n'existe pas."}</p>
          <button
            onClick={() => navigate('/pilgrimages')}
            className='bg-church-brown text-white px-6 py-3 rounded-md hover:bg-church-dark transition-colors font-medium'
          >
            Retour aux albums
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen'>
      {/* Hero header cropped to 1/3 */}
      <HeroBackground size='third' />
      
      {/* Page Title */}
      <PageHeader title={album.name} subtitle={album.description} />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Album Information Card */}
        <div className='bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 p-4 mb-8'>
          <div className='flex items-start justify-between mb-2'>
            <h2 className='text-xl font-semibold text-gray-900 pr-2'>{album.name}</h2>
            <ShareButton />
          </div>
          {album.description && (
            <p className='text-gray-600 text-sm mt-1 mb-3'>{album.description}</p>
          )}
          <div className='flex flex-col gap-2 text-xs text-gray-500'>
            <span>{album.media_count} média(s)</span>
            {album.author && (
              <span>Par {album.author.full_name || album.author.username}</span>
            )}
            {album.date && (
              <span>{new Date(album.date).toLocaleDateString('fr-FR')}</span>
            )}
          </div>
        </div>

        {/* Masonry Layout */}
        {mediaItems.length === 0 ? (
          <div className='text-center py-16'>
            <Inbox className='w-10 h-10 mx-auto mb-4 text-gray-300' />
            <p className='text-gray-600 text-lg'>
              Cet album ne contient aucun média
            </p>
          </div>
        ) : (
          <div
            className='gap-2'
            style={{
              columnCount: columnCount,
              columnGap: '0.5rem',
            }}
          >
            {mediaItems.map((media) => {
              const mediaUrl = apiService.getAlbumMediaUrl(media.id);

              return (
                <div key={media.id} className='break-inside-avoid mb-2'>
                  {media.file_type === 'image' ? (
                    brokenImages.has(media.id) ? (
                      <div
                        className='w-full flex border flex-col items-center justify-center cursor-pointer p-4 border-gray-300 bg-gray-50 rounded-lg'
                        onClick={() => handleMediaClick(media)}
                      >
                        <div className='text-gray-400 mb-2'>
                          <svg className='w-8 h-8' fill='currentColor' viewBox='0 0 24 24'>
                            <path d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z' />
                          </svg>
                        </div>
                        <div className='text-gray-500 text-xs text-center'>
                          <div>Image non disponible</div>
                          <div className='text-gray-400'>Nom: {media.filename}</div>
                          <div className='text-gray-400'>ID: {media.id}</div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className='relative cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-church-gold/50'
                        onClick={() => handleMediaClick(media)}
                      >
                        <img
                          src={mediaUrl}
                          alt={media.title || media.filename}
                          className='w-full h-auto object-contain'
                          loading='lazy'
                          onError={() => handleImageError(media.id)}
                        />
                        {(media.title || media.description) && (
                          <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-3'>
                            {media.title && (
                              <h4 className='text-sm font-medium text-white mb-1'>
                                {media.title}
                              </h4>
                            )}
                            {media.description && (
                              <p className='text-xs text-white/90 line-clamp-2'>
                                {media.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <div
                      className='w-full bg-gray-900 flex items-center justify-center cursor-pointer hover:bg-gray-800 transition-colors relative rounded-lg border border-gray-200 min-h-[200px]'
                      onClick={() => handleMediaClick(media)}
                    >
                      <div className='text-white text-center p-4'>
                        <div className='text-4xl mb-2'>▶️</div>
                        <p className='text-sm'>{media.title || 'Vidéo'}</p>
                        {media.description && (
                          <p className='text-xs text-white/70 mt-1'>{media.description}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Back Button */}
        <div className='mt-12 pt-8 border-t border-gray-200'>
          <button
            onClick={() => navigate('/pilgrimages')}
            className='bg-church-brown text-white px-6 py-3 rounded-md hover:bg-church-dark transition-colors font-medium inline-flex items-center gap-2'
          >
            <ArrowLeft className='w-4 h-4' /> Retour aux albums
          </button>
        </div>
      </div>

      {/* Media Modal */}
      <MediaModal media={selectedMedia} onClose={closeModal} />
    </div>
  );
};
